import React, { useState, useRef } from 'react';
import { Star, X } from 'lucide-react';
import Loader from '../../Components/utils/Loader';

const questions = [
  "How knowledgeable was the seller?",
  "Were the deadlines met?",
  "How was the quality of products delivered?",
  "Would you recommend this seller to other buyers?",
  "How quick the seller responded to your queries?",
  "Did the seller meet your expectations?",
];

const ReviewPopup = ({ review, onClose, onSubmit }) => {
  const [ratings, setRatings] = useState(Array(6).fill(0));
  const [comment, setComment] = useState('');
  const [attachedImages, setAttachedImages] = useState([]);
  const [error, setError] = useState('');
  const [submissionStatus, setSubmissionStatus] = useState('form'); // 'form', 'loading', 'success', 'error'
  const [submissionError, setSubmissionError] = useState('');
  const fileInputRef = useRef(null);

  const handleRatingChange = (index, score) => {
    const newRatings = [...ratings];
    newRatings[index] = score;
    setRatings(newRatings);
  };

  const getTextForScore = (score) => {
    switch (score) {
      case 1: return "Very Unsatisfied";
      case 2: return "Unsatisfied";
      case 3: return "Neutral";
      case 4: return "Satisfactory";
      case 5: return "Very Satisfied";
      default: return "Not rated";
    }
  };

  const handleSubmit = async () => {
    if (ratings.some(r => r === 0)) {
      setError('Please provide ratings for all questions.');
      return;
    }
    if (!comment.trim()) {
      setError('Please provide a comment.');
      return;
    }
    setError('');
    setSubmissionStatus('loading');
    try {
      await onSubmit({
        ratings,
        comment,
        attachedImages,
      });
      setSubmissionStatus('success');
    } catch (error) {
      setSubmissionStatus('error');
      setSubmissionError('Failed to submit review. Please try again.');
    }
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    setAttachedImages([...attachedImages, ...files].slice(0, 5));
  };

  const removeImage = (index) => {
    setAttachedImages(attachedImages.filter((_, i) => i !== index));
  };

  if (submissionStatus === 'success') {
    return (
      <div className="modalOverlay">
        <div className="modalContent" style={{textAlign: "center"}}>
          <p>The review has been submitted. Thank you for your review!</p>
          <button onClick={onClose} className="modalButton cancel" style={{marginTop: "10px 0px"}}>Close</button>
        </div>
      </div>
    );
  } else if (submissionStatus === 'error') {
    return (
      <div className="reviewPopup">
        <div className="reviewPopupContent">
          <h2>Error</h2>
          <p>{submissionError}</p>
          <button onClick={() => setSubmissionStatus('form')} className="modalButton">Try Again</button>
          <button onClick={onClose} className="modalButton">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="reviewPopup">
      <div className="reviewPopupContent">
        <h2>Leave Review</h2>
        <div className="reviewPopupHeader">
          <img src={review.image} alt={review.title} className="reviewPopupProductImage" />
          <div className="reviewPopupProductInfo">
            <p style={{ fontWeight: "500", marginBottom: "8px" }}>{review.title}</p>
            <p style={{ fontSize: "13px" }}>By {review.sellerName}</p>
          </div>
        </div>
        <div className="reviewPopupBody">
          <div className="reviewPopupMain">
            <div className="ratingsSection">
              {questions.map((question, index) => (
                <div key={index} className="ratingRow">
                  <span>{question}</span>
                  <div className="stars">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        size={20}
                        onClick={() => handleRatingChange(index, star)}
                        fill={star <= ratings[index] ? "gold" : "none"}
                        stroke={star <= ratings[index] ? "gold" : "currentColor"}
                        className='star'
                      />
                    ))}
                    <span>{getTextForScore(ratings[index])}</span>
                  </div>
                </div>
              ))}
            </div>
            <textarea
              placeholder="Add comments"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="inputField"
            />
            <div className="imageUploadSection">
              <h4 className='subHeading'>Upload Images</h4>
              <p className='infoSection'>
                • Can add up to 5 images<br />
                • Maximum image size is 5 MB
              </p>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                ref={fileInputRef}
              />
              <div className="imageUploadPlaceholderContainer">
                <div className='imageUploadPlaceholder' onClick={() => fileInputRef.current.click()}>+</div>
                <div className="attachedImagesGallery">
                  {attachedImages.map((image, index) => (
                    <div key={index} className="attachedImageContainer">
                      <img src={URL.createObjectURL(image)} alt={`Attached ${index}`} />
                      <X className="removeIcon" onClick={() => removeImage(index)} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        {error && <p className="errorMessage">{error}</p>}
        <div className="popupButtons">
          <button onClick={onClose} className="modalButton cancel">Close</button>
          <button onClick={handleSubmit} className="modalButton submit">Submit Review</button>
        </div>
      </div>
      {submissionStatus === 'loading' && <Loader type="checkmate" />}
    </div>
  );
};

export default ReviewPopup;